<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>{{ $t("customers_crud.customers") }}</v-toolbar-title>
      <v-divider class="mx-2" inset vertical></v-divider>
      <v-text-field
        v-model="filter"
        append-icon="search"
        :label="$t('customers_crud.search')"
        single-line
        hide-details
        autofocus
        @input="isChangingFilter = true"
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-dialog v-model="dialog" max-width="1000">
        <v-card>
          <v-card-title>
            <span class="headline">{{ formTitle }}</span>
            <v-spacer></v-spacer>
            <!-- Add an address form -->
            <contact-create
              :customer="editedItem"
              v-if="editedIndex != -1"
            ></contact-create>
            <address-create
              @addressUploaded="updateAddressesList"
              :customer="editedItem"
              :countryCodes="countryCodes"
              v-if="editedIndex != -1"
            ></address-create>
          </v-card-title>

          <v-card-text v-if="showDialog">
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.name"
                    :label="$t('customers_crud.name')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.vat_number"
                    :label="$t('customers_crud.vat')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.mobile"
                    :label="$t('customers_crud.mobile')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.phone"
                    :label="$t('customers_crud.phone')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.email"
                    :label="$t('customers_crud.email')"
                  ></v-text-field>
                </v-flex>
                <v-flex v-if="editedIndex != -1" xs12 sm6 md4>
                  <address-select
                    ref="addressSelectMain"
                    type="main"
                    :items="addresses"
                    :customer="editedItem"
                    :label="$t('customers_crud.main_address')"
                    @addressSelected="changeAddress"
                  ></address-select>
                </v-flex>
                <v-flex xs12 sm6 md4 v-if="editedIndex != -1">
                  <address-select
                    ref="addressSelectDefault"
                    :type="'default'"
                    :items="addresses"
                    :customer="editedItem"
                    :label="$t('customers_crud.default_address')"
                    @addressSelected="changeAddress"
                  ></address-select>
                </v-flex>
                <v-flex xs12 sm6 md4 v-if="editedIndex != -1">
                  <packing-type-select
                    :packingTypes="packingTypes"
                    @packingTypeSelected="changePackingType"
                  ></packing-type-select>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.code"
                    :label="$t('customers_crud.code')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.pricing"
                    :label="$t('customers_crud.pricing')"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.default_discount"
                    type="number"
                    min="0"
                    step="0.1"
                    :label="$t('customers_crud.default_discount')"
                  ></v-text-field>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" flat @click="close">
              {{ $t("customers_crud.cancel") }}
            </v-btn>
            <v-btn
              color="blue darken-1"
              flat
              @click="addCustomer"
              v-if="editedIndex == -1"
              >{{ $t("customers_crud.save") }}</v-btn
            >
            <v-btn
              color="blue darken-1"
              flat
              @click="editCustomer"
              v-if="editedIndex != -1"
              >{{ $t("customers_crud.edit") }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>

    <div class="table-wrapper">
      <div class="progress-wrapper">
        <v-progress-linear :indeterminate="loading"></v-progress-linear>
      </div>
      <div class="rTable">
        <div class="rTableRow problem-header">
          <div
            v-for="(header, i) in headers"
            :key="i"
            class="rTableHead unselectable"
            :class="{ clickable: header.value }"
            :style="{ width: header.width + '%' }"
            @click="changeSort(header.value)"
          >
            <span>{{ header.text }}</span>
            <span v-if="header.value === sortBy">
              <v-icon small v-if="descending">north</v-icon>
              <v-icon small v-else>south</v-icon>
            </span>
          </div>
        </div>
        <div class="rTableRow" v-for="(customer, i) in customers" :key="i">
          <div class="rTableCell">{{ customer.name }}</div>
          <div class="rTableCell">{{ customer.code }}</div>
          <div class="rTableCell">{{ customer.vat_number }}</div>
          <div class="rTableCell">{{ customer.mobile }}</div>
          <div class="rTableCell">{{ customer.phone }}</div>
          <div class="rTableCell">{{ customer.email }}</div>
          <div class="rTableCell">{{ formAddress(customer.main_address) }}</div>
          <div class="rTableCell">
            {{ formAddress(customer.default_delivery_address) }}
          </div>
          <div class="rTableCell">
            {{ packingTypeName(customer.pref_packing_type) }}
          </div>
          <div class="rTableCell">{{ customer.pricing }}</div>
          <div class="rTableCell">{{ customer.default_discount }}</div>
          <div class="rTableCell">
            <v-icon
              small
              class="mr-2"
              v-tooltip="$t('contacts_crud.view_contacts')"
              @click="openCustomerContacts(customer.id)"
              >contacts</v-icon
            >
            <v-icon
              small
              class="mr-2"
              v-tooltip="$t('addresses_crud.view_addresses')"
              @click="openCustomerAddresses(customer.id)"
              >book</v-icon
            >
            <customer-templates :customer="customer"></customer-templates>
            <v-icon
              small
              class="mr-2"
              v-tooltip="$t('customers_crud.edit')"
              @click="getWorkingItem(customer)"
              >edit</v-icon
            >
            <v-icon
              small
              v-tooltip="$t('customers_crud.delete')"
              @click="deleteCustomer(customer)"
              >delete</v-icon
            >
          </div>
        </div>
      </div>
    </div>
    <div class="text-xs-center pt-2">
      <v-pagination v-model="page" :length="pages"></v-pagination>
    </div>
    <contacts-list ref="contactsListDialog" />
    <addresses-list ref="addresesListDialog" :countryCodes="countryCodes" />
  </div>
</template>

<script>
import AddressCreate from "@/components/admin/addresses/AddressCreate.vue";
import AddressSelect from "@/components/admin/addresses/AddressSelect.vue";
import PackingTypeSelect from "@/components/admin/packing-types/PackingTypeSelector.vue";
import Customer from "@/models/Customer.js";
import Address from "@/models/Address.js";
import PackingType from "@/models/PackingType.js";
import ContactCreate from "@/components/admin/contacts/ContactCreator.vue";
import ContactsList from "@/components/admin/contacts/ContactsList.vue";
import AddressesList from "@/components/admin/addresses/AddressesList.vue";
import CustomerTemplates from "@/components/admin/customers/CustomerTemplatesManager.vue";
import CountryCode from "@/models/CountryCode.js";
import User from "@/models/User.js";

export default {
  data() {
    return {
      user: new User(this.$session.get("user")),
      isChangingFilter: false,
      filterTimer: 0,
      customers: [],
      countryCodes: [],
      addressesLoaded: false,
      packingTypesLoaded: false,
      dialog: false,
      loading: true,
      headers: getHeaders(this),
      sortBy: "name",
      descending: false,
      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 50, // -1 for All
        sortBy: null,
        totalItems: 0,
      },
      pages: 1,
      filter: this.getFilterFromRoute(this.$route.params.filter),
      editedIndex: -1,
      addresses: [],
      packingTypes: [],
      page: 1,
      editedItem: {
        name: "",
        vat_number: "",
        ref: "",
        mobile: "",
        phone: "",
        email: "",
        main_address: "",
        default_delivery_address: "",
        pref_packing_type: "",
        code: "",
        pricing: "",
        default_discount: ""
      },
      defaultItem: {
        name: "",
        vat_number: "",
        ref: "",
        mobile: "",
        phone: "",
        email: "",
        main_address: "",
        default_delivery_address: "",
        pref_packing_type: "",
        code: "",
        pricing: "",
        default_discount: ""
      },
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? this.$t("customers_crud.new_customer")
        : this.$t("customers_crud.edit_customer");
    },
    showDialog() {
      return this.addressesLoaded && this.packingTypesLoaded;
    },
  },

  async mounted() {
    const codes = await CountryCode.loadCountryCodes({
      params: {
        language: this.$route.params.lang,
      },
    });
    this.countryCodes = this.prepareCountryCodesForSelect(codes);
    this.initialize();
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    filter() {
      clearTimeout(this.filterTimer);
      this.filterTimer = setTimeout(() => {
        this.isChangingFilter = false;
      }, 500);
    },
    isChangingFilter(val) {
      if (!val) {
        this.redirect();
        this.initialize();
      }
    },
    page() {
      this.initialize();
    },
  },

  methods: {
    async initialize() {
      this.loading = true;
      const {
        customers,
        page,
        pagesCount,
        itemsCount,
      } = await Customer.loadCustomers({
        params: {
          page: this.page,
          filter: this.filter,
          order: this.descending ? "desc" : "asc",
          order_by: this.sortBy,
        },
      });
      if (itemsCount != 0) {
        this.page = page;
        this.pagination.totalItems = itemsCount;
        this.pages = pagesCount;
      } else {
        this.page = 1;
        this.pagination.totalItems = 0;
        this.pagination.pages = 1;
      }
      this.customers = customers;
      this.loading = false;
    },

    changeSort(header) {
      if (!header) return;
      if (this.sortBy === header) {
        this.descending = !this.descending;
      } else {
        this.sortBy = header;
        this.descending = false;
      }
      this.initialize();
    },

    async openCustomerAddresses(customerId) {
      const customer = await Customer.loadCustomer(customerId);
      this.$refs.addresesListDialog.openAndPrepare(customer);
    },

    async openCustomerContacts(customerId) {
      const customer = await Customer.loadCustomer(customerId);
      this.$refs.contactsListDialog.openAndPrepare(customer);
    },

    /**
     * gets filter from router link
     * @param filter
     */
    getFilterFromRoute(filter) {
      if (typeof filter == "string" && filter == "null") {
        return null;
      }
      if (filter == null) {
        return null;
      }
      return filter;
    },

    addCustomer() {
      this.close();
    },

    async editCustomer() {
      await this.editedItem.save(formParams(this.editedItem));
      this.initialize();
      this.close();
    },

    async deleteCustomer(customer) {
      if (!this.user.hasRights("CA_DEL")) {
        alert(this.$t("customers_crud.no_rights_to_delete_customer"));
        return 0;
      }
      const deletingCustomer = new Customer(customer);
      confirm(this.$t("customers_crud.delete_confirm")) && "";
      await deletingCustomer.delete();
      this.initialize();
    },

    getWorkingItem(item) {
      this.editedIndex = this.customers.indexOf(item);
      if (this.editedIndex != -1 && !this.user.hasRights("CA_EDIT")) {
        alert(this.$t("customers_crud.no_rights_to_edit_customer"));
        return 0;
      }
      this.editedItem = new Customer(item);
      if (this.editedIndex != -1) {
        this.getAddresses();
        this.getPackingTypes();
      }
      this.dialog = true;
    },

    close() {
      this.dialog = false;
      this.addressesLoaded = false;
      this.packingTypesLoaded = false;
      this.addresses = [];
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },

    reset() {
      this.filter = null;
      this.initialize();
    },

    formAddress(address) {
      if (address == null) return "";
      return (
        (address.city || "") +
        ", " +
        (address.street || "") +
        " " +
        (address.zip_code || "") +
        " " +
        (address.country || "")
      );
    },

    packingTypeName(packingType) {
      if (packingType == null) {
        return "";
      }
      return packingType.description;
    },

    async getAddresses() {
      this.addresses = await Address.loadAddresses({
        params: {
          customer_id: this.editedItem.id,
        },
      });
      this.addressesLoaded = true;
    },

    async getPackingTypes() {
      this.packingTypes = await PackingType.loadPackingTypes();
      this.packingTypesLoaded = true;
    },

    changeAddress(address) {
      if (address.type == "main") {
        this.editedItem.main_address_to_set = address.value;
      }
      if (address.type == "default") {
        this.editedItem.default_address_to_set = address.value;
      }
    },

    changePackingType(packingType) {
      this.editedItem["packingType"] = packingType;
    },

    updateAddressesList() {
      this.getAddresses();
      this.$refs.addressSelectMain.display();
      this.$refs.addressSelectDefault.display();
    },

    /**
     * changes router link
     */
    redirect() {
      const filter =
        this.filter != null && this.filter.length > 0 ? this.filter : null;
      this.$router
        .push({
          path: `/${this.$router.currentRoute.params.lang}/customers/${filter}`,
        })
        .catch(() => {});
    },

    prepareCountryCodesForSelect(codes) {
      let preparedCodes = [];
      codes.forEach((code) => {
        preparedCodes.push({
          text: code.toString(),
          value: code.country_code,
        });
      });
      return preparedCodes;
    },
  },
  components: {
    AddressCreate,
    AddressSelect,
    AddressesList,
    PackingTypeSelect,
    ContactCreate,
    ContactsList,
    CustomerTemplates,
  },
};

function formParams(customer) {
  let params = {
    'pricing': customer.pricing,
    'default_discount': customer.default_discount
  };
  if (customer["code"] != null) params["code"] = customer["code"];
  if (customer["email"] != null) params["email"] = customer["email"];
  if (customer["mobile"] != null) params["mobile"] = customer["mobile"];
  if (customer["name"] != null) params["name"] = customer["name"];
  if (customer["phone"] != null) params["phone"] = customer["phone"];
  if (customer["ref"] != null) params["ref"] = customer["ref"];
  if (customer["type"] != null) params["type"] = customer["type"];
  if (customer["vat_number"] != null)
    params["vat_number"] = customer["vat_number"];
  if (customer["main_address_to_set"] != null)
    params["main_address"] = parseInt(customer["main_address_to_set"]);
  if (customer["default_address_to_set"] != null)
    params["default_delivery_address"] = parseInt(
      customer["default_address_to_set"]
    );
  if (customer.packingType != null) {
    params["pref_packing_type"] = parseInt(customer.packingType);
  }
  return params;
}

function getHeaders(vue) {
  return [
    {
      text: vue.$t("customers_crud.name"),
      align: "left",
      sortable: false,
      value: "name",
      width: 8,
    },
    {
      text: vue.$t("customers_crud.code"),
      value: "code",
      sortable: false,
      width: 5,
    },
    {
      text: vue.$t("customers_crud.vat"),
      value: "vat_number",
      sortable: false,
      width: 5,
    },
    {
      text: vue.$t("customers_crud.mobile"),
      value: "mobile",
      sortable: false,
      width: 8,
    },
    {
      text: vue.$t("customers_crud.phone"),
      value: "phone",
      sortable: false,
      width: 12,
    },
    {
      text: vue.$t("customers_crud.email"),
      value: "email",
      sortable: false,
      width: 14,
    },
    {
      text: vue.$t("customers_crud.main_address"),
      value: "main_address",
      sortable: false,
      width: 10,
    },
    {
      text: vue.$t("customers_crud.default_address"),
      value: "default_delivery_address",
      sortable: false,
      width: 10,
    },
    {
      text: vue.$t("customers_crud.pref_packing_type"),
      value: "pref_packing_type",
      sortable: false,
      width: 10,
    },
    {
      text: vue.$t("customers_crud.pricing"),
      value: "pricing",
      sortable: false,
      width: 5,
    },
    {
      text: vue.$t("customers_crud.default_discount"),
      value: "default_discount",
      sortable: false,
      width: 5,
    },
    { text: vue.$t("customers_crud.actions"), sortable: false, width: 10 },
  ];
}
</script>

<style scoped>
.progress-wrapper {
  height: 7px;
}
</style>
